import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, Trophy, Clock, Target, BookOpen, Star, AlertCircle, CheckCircle, Feather, Zap, Flame } from 'lucide-react';
import exerciseService from '../services/exerciseService';
import './ExerciseGenerator.css';

const ExerciseGenerator = () => {
    const [topic, setTopic] = useState('');
    const [exercises, setExercises] = useState([]);
    const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [isAnswerChecked, setIsAnswerChecked] = useState(false);
    const [difficulty, setDifficulty] = useState('medium');
    const [exerciseCount, setExerciseCount] = useState(5);
    const [timer, setTimer] = useState(0);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [score, setScore] = useState({ correct: 0, total: 0 });
    const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
    const [userAnswers, setUserAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 }); // NEW: Progress tracking

    // Timer effect
    useEffect(() => {
        let interval = null;
        if (isTimerRunning) {
            interval = setInterval(() => {
                setTimer(timer => timer + 1);
            }, 1000);
        } else if (!isTimerRunning && timer !== 0) {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isTimerRunning, timer]);

    // Format time display
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // 🚀 Updated quick topics with more variety
    const quickTopics = [
        'Present Perfect', 'Past Simple', 'Conditional Sentences', 'Modal Verbs',
        'Passive Voice', 'Gerund and Infinitive', 'Relative Clauses', 'Articles',
        'Future Tenses', 'Phrasal Verbs', 'Reported Speech', 'Subjunctive'
    ];

        const difficulties = [
        { value: 'easy', label: 'Cơ bản', icon: Feather, color: 'text-green-400' },
        { value: 'medium', label: 'Trung cấp', icon: Zap, color: 'text-yellow-400' },
        { value: 'hard', label: 'Nâng cao', icon: Flame, color: 'text-red-400' }
    ];

    const handleGenerate = async () => {
        // 🔍 Enhanced validation
        const validation = exerciseService.validateBatchRequest(topic.trim(), difficulty, exerciseCount);
        if (!validation.isValid) {
            setError(validation.errors.join('. '));
            return;
        }

        setIsLoading(true);
        setError('');
        setExercises([]);
        setCurrentExerciseIndex(0);
        setSelectedOption(null);
        setIsAnswerChecked(false);
        setScore({ correct: 0, total: 0 });
        setAnsweredQuestions(new Set());
        setUserAnswers({});
        setShowResults(false);
        setTimer(0);
        setIsTimerRunning(true);
        setGenerationProgress({ current: 0, total: exerciseCount });

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Bạn cần đăng nhập để sử dụng chức năng này.');
                setIsLoading(false);
                setIsTimerRunning(false);
                return;
            }

            // 🔥 Enhanced API call with better error handling
            const response = await exerciseService.generateExerciseBatch(topic.trim(), difficulty, exerciseCount, token);



            // 🎯 Handle the new response format
            let generatedExercises = [];
            if (response.exercises && Array.isArray(response.exercises)) {
                // New optimized API format
                generatedExercises = response.exercises;

                // Show generation info
                if (response.meta) {

                    if (response.meta.generated < response.meta.requested) {
                        setError(`⚠️ Chỉ tạo được ${response.meta.generated}/${response.meta.requested} bài tập. Các bài tập vẫn có thể sử dụng.`);
                    }
                }
            } else if (response.data && Array.isArray(response.data)) {
                // Legacy format support
                generatedExercises = response.data;
            } else if (Array.isArray(response)) {
                // Direct array format
                generatedExercises = response;
            } else {
                // Single exercise format - fallback
                generatedExercises = [response];
            }

            if (generatedExercises.length === 0) {
                throw new Error('Không thể tạo bài tập nào. Vui lòng thử chủ đề khác hoặc giảm số lượng câu hỏi.');
            }



            // 🆔 Add unique IDs and validation
            const exercisesWithIds = generatedExercises
                .filter(exercise => exercise && exercise.question && exercise.options && exercise.correct_answer)
                .map((exercise, index) => ({
                    ...exercise,
                    frontendId: `ex_${Date.now()}_${index}`,
                    originalIndex: index
                }));

            if (exercisesWithIds.length === 0) {
                throw new Error('Các bài tập được tạo không hợp lệ. Vui lòng thử lại.');
            }

            setExercises(exercisesWithIds);
            setGenerationProgress({ current: exercisesWithIds.length, total: exerciseCount });

            // 🎊 Success message
            if (exercisesWithIds.length < exerciseCount) {
                setError(`⚠️ Tạo được ${exercisesWithIds.length}/${exerciseCount} bài tập hợp lệ.`);
            }

        } catch (err) {
            console.error('Exercise generation error:', err);

            // 🚨 Enhanced error handling
            let errorMessage = 'Đã có lỗi xảy ra. Vui lòng thử lại.';

            if (err.message) {
                errorMessage = err.message;
            } else if (err.error) {
                errorMessage = err.error;
            } else if (typeof err === 'string') {
                errorMessage = err;
            }

            // Specific error messages
            if (errorMessage.includes('rate limit') || errorMessage.includes('429')) {
                errorMessage = '⏱️ API đang bận. Vui lòng đợi 1-2 phút rồi thử lại.';
            } else if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
                errorMessage = '🌐 Lỗi kết nối mạng. Vui lòng kiểm tra internet và thử lại.';
            } else if (errorMessage.includes('token') || errorMessage.includes('401')) {
                errorMessage = '🔒 Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
            }

            setError(errorMessage);
            setIsTimerRunning(false);
        }
        setIsLoading(false);
    };

    const handleOptionClick = (optionKey) => {
        if (!isAnswerChecked && !answeredQuestions.has(currentExerciseIndex)) {
            setSelectedOption(optionKey);
        }
    };

    const checkAnswer = async () => {
        if (selectedOption && !answeredQuestions.has(currentExerciseIndex)) {
            setIsAnswerChecked(true);
            const currentExercise = exercises[currentExerciseIndex];
            const isCorrect = selectedOption === currentExercise.correct_answer;

            setUserAnswers(prev => ({
                ...prev,
                [currentExerciseIndex]: {
                    selected: selectedOption,
                    correct: isCorrect,
                    timeSpent: timer // Track time per question
                }
            }));

            setAnsweredQuestions(prev => new Set([...prev, currentExerciseIndex]));

            if (isCorrect) {
                setScore(prev => ({ ...prev, correct: prev.correct + 1, total: prev.total + 1 }));
            } else {
                setScore(prev => ({ ...prev, total: prev.total + 1 }));
            }

            // 📊 REMOVED: Submit results to backend (causing 404)
            // Will add back when backend route is ready
            /*
            try {
                const token = localStorage.getItem('token');
                if (token && currentExercise.id) {
                    await exerciseService.submitResults(
                        currentExercise.id, 
                        selectedOption, 
                        timer, 
                        token
                    );
                }
            } catch (error) {
                console.warn('Could not submit results:', error);
            }
            */

            // Check if all questions answered
            if (answeredQuestions.size + 1 === exercises.length) {
                setIsTimerRunning(false);
                setTimeout(() => setShowResults(true), 1500);
            }
        }
    };

    const nextExercise = () => {
        if (currentExerciseIndex < exercises.length - 1) {
            setCurrentExerciseIndex(currentExerciseIndex + 1);
            setSelectedOption(userAnswers[currentExerciseIndex + 1]?.selected || null);
            setIsAnswerChecked(answeredQuestions.has(currentExerciseIndex + 1));
        }
    };

    const prevExercise = () => {
        if (currentExerciseIndex > 0) {
            setCurrentExerciseIndex(currentExerciseIndex - 1);
            setSelectedOption(userAnswers[currentExerciseIndex - 1]?.selected || null);
            setIsAnswerChecked(answeredQuestions.has(currentExerciseIndex - 1));
        }
    };

    const resetQuiz = () => {
        setExercises([]);
        setCurrentExerciseIndex(0);
        setSelectedOption(null);
        setIsAnswerChecked(false);
        setScore({ correct: 0, total: 0 });
        setAnsweredQuestions(new Set());
        setUserAnswers({});
        setShowResults(false);
        setTimer(0);
        setIsTimerRunning(false);
        setError('');
        setGenerationProgress({ current: 0, total: 0 });
    };

    const currentExercise = exercises[currentExerciseIndex];
    const progress = exercises.length > 0 ? ((currentExerciseIndex + 1) / exercises.length) * 100 : 0;
    const accuracy = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;

    // 🎯 Results screen with enhanced stats
    if (showResults) {
        const avgTimePerQuestion = exercises.length > 0 ? Math.round(timer / exercises.length) : 0;
        const performanceLevel = accuracy >= 80 ? 'Xuất sắc' : accuracy >= 60 ? 'Khá tốt' : accuracy >= 40 ? 'Trung bình' : 'Cần cải thiện';
        const performanceColor = accuracy >= 80 ? 'text-green-600' : accuracy >= 60 ? 'text-green-500' : accuracy >= 40 ? 'text-red-400' : 'text-red-600';

        return (
            <div className="exercise-generator-container">
                <div className="exercise-generator-content">
                    <div className="exercise-generator-inner">
                        <div className="text-center">
                    <Trophy className="w-20 h-20 mx-auto mb-6 text-green-600" />
                    <h2 className="text-4xl font-bold mb-2 text-gray-800">
                        Kết Quả Bài Tập
                    </h2>
                    <p className={`text-xl mb-6 ${performanceColor} font-semibold`}>
                        {performanceLevel}!
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <div className="p-6 rounded-xl bg-blue-50 border border-blue-200">
                            <Target className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                            <div className="text-2xl font-bold text-blue-700">
                                {score.correct}/{score.total}
                            </div>
                            <div className="text-sm text-blue-600">
                                Câu đúng
                            </div>
                        </div>

                        <div className="p-6 rounded-xl bg-green-50 border border-green-200">
                            <Star className="w-8 h-8 mx-auto mb-2 text-green-600" />
                            <div className="text-2xl font-bold text-green-700">
                                {accuracy}%
                            </div>
                            <div className="text-sm text-green-600">
                                Độ chính xác
                            </div>
                        </div>

                        <div className="p-6 rounded-xl bg-orange-50 border border-orange-200">
                            <Clock className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                            <div className="text-2xl font-bold text-orange-700">
                                {formatTime(timer)}
                            </div>
                            <div className="text-sm text-orange-600">
                                Tổng thời gian
                            </div>
                        </div>

                        <div className="p-6 rounded-xl bg-purple-50 border border-purple-200">
                            <Clock className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                            <div className="text-2xl font-bold text-purple-700">
                                {avgTimePerQuestion}s
                            </div>
                            <div className="text-sm text-purple-600">
                                TB/câu
                            </div>
                        </div>
                    </div>

                    {/* 📊 Detailed breakdown */}
                    <div className="mb-6 p-4 rounded-xl bg-gray-50 border border-gray-200">
                        <h3 className="text-lg font-semibold mb-2 text-gray-800">
                            📈 Chi tiết bài làm
                        </h3>
                        <p className="text-gray-600">
                            Chủ đề: <span className="font-semibold">{topic}</span> •
                            Độ khó: <span className="font-semibold">{difficulties.find(d => d.value === difficulty)?.label}</span> •
                            Tổng câu: <span className="font-semibold">{exercises.length}</span>
                        </p>
                    </div>

                    <div className="flex gap-4 justify-center">
                        <button
                            onClick={resetQuiz}
                            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all transform hover:scale-105"
                        >
                            <RotateCcw className="w-5 h-5" />
                            Làm Lại
                        </button>
                        <button
                            onClick={() => setShowResults(false)}
                            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all transform hover:scale-105"
                        >
                            <BookOpen className="w-5 h-5" />
                            Xem Lại Câu Hỏi
                        </button>
                    </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="exercise-generator-container">
            <div className="exercise-generator-content">
                <div className="exercise-generator-inner">
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-800">
                    🧠 Tạo Bài Tập Ngữ Pháp AI
                </h2>
            </div>

            {/* Configuration Panel */}
            {exercises.length === 0 && (
                <div className="mb-6">
                    {/* Main Grid Layout - 2 columns */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
                        {/* Left Column - Topic */}
                        <div>
                            <label className="block text-xl font-semibold mb-4 text-gray-800">
                                📚 Chủ đề ngữ pháp
                            </label>
                            <input
                                type="text"
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                placeholder="VD: Present Perfect, Conditional Sentences..."
                                className="w-full p-4 text-lg rounded-lg border-2 transition-colors bg-white border-gray-300 text-gray-800 focus:border-blue-500 focus:ring-blue-500"
                            />

                            {/* Quick Topics */}
                            <div className="mt-3">
                                <p className="text-sm mb-2 text-gray-600">
                                    💡 Gợi ý nhanh:
                                </p>
                                <div className="grid grid-cols-4 gap-3">
                                    {quickTopics.slice(0, 8).map((quickTopic) => (
                                        <button
                                            key={quickTopic}
                                            onClick={() => setTopic(quickTopic)}
                                            className="px-4 py-3 text-base font-medium rounded-lg transition-colors bg-gray-200 text-gray-700 hover:bg-blue-100 hover:text-blue-700"
                                        >
                                            {quickTopic}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Settings */}
                        <div className="space-y-5">
                            {/* Difficulty */}
                            <div>
                                <label className="block text-xl font-semibold mb-4 text-gray-800">
                                    🎯 Độ khó
                                </label>
                                <div className="grid grid-cols-3 gap-4">
                                    {difficulties.map((d) => (
                                        <label
                                            key={d.value}
                                            className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 flex flex-col items-center justify-center text-center
                                            ${difficulty === d.value
                                                ? `border-blue-500 bg-blue-50 shadow-blue-500/20 shadow-lg`
                                                : 'border-gray-300 bg-white hover:border-blue-400'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="difficulty"
                                                value={d.value}
                                                checked={difficulty === d.value}
                                                onChange={(e) => setDifficulty(e.target.value)}
                                                className="absolute opacity-0 w-full h-full cursor-pointer"
                                            />
                                            <d.icon className={`w-8 h-8 mb-2 ${d.color}`} />
                                            <span className="font-semibold text-base text-gray-800">
                                                {d.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Exercise Count */}
                            <div>
                                <label className="block text-xl font-semibold mb-4 text-gray-800">
                                    📝 Số câu hỏi
                                </label>
                                <select
                                    value={exerciseCount}
                                    onChange={(e) => setExerciseCount(Number(e.target.value))}
                                    className="w-full p-4 text-lg rounded-lg border-2 bg-white border-gray-300 text-gray-800 focus:outline-none focus:border-blue-500"
                                >
                                    <option value={3}>3 câu (Nhanh - 30s)</option>
                                    <option value={5}>5 câu (Chuẩn - 1 phút)</option>
                                    <option value={10}>10 câu (Mở rộng - 2 phút)</option>
                                    <option value={15}>15 câu (Chuyên sâu - 3 phút)</option>
                                    <option value={20}>20 câu (Toàn diện - 4 phút)</option>
                                </select>
                                <p className="text-xs mt-1 text-gray-500">
                                    💡 Thời gian ước tính để AI tạo bài tập
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Generate Button */}
                    <button
                        onClick={handleGenerate}
                        disabled={isLoading}
                        className="w-full md:w-auto bg-red-600 text-white font-bold text-xl py-5 px-12 rounded-xl hover:bg-red-700 transition-transform transform hover:scale-105 disabled:bg-gray-500 disabled:scale-100"
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center space-x-2">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                <span>🤖 AI đang tạo {exerciseCount} bài tập...</span>
                            </div>
                        ) : (
                            `✨ Tạo ${exerciseCount} Bài Tập Với AI`
                        )}
                    </button>

                    {/* 📊 Generation progress */}
                    {isLoading && generationProgress.total > 0 && (
                        <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
                            <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-gray-600">
                                    Tiến trình tạo bài tập
                                </span>
                                <span className="text-blue-600">
                                    {generationProgress.current}/{generationProgress.total}
                                </span>
                            </div>
                            <div className="w-full h-2 rounded-full bg-gray-300">
                                <div
                                    className="h-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300"
                                    style={{ width: `${(generationProgress.current / generationProgress.total) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Error Message with enhanced styling */}
            {error && (
                <div className={`mb-6 p-4 rounded-xl border-l-4 ${error.includes('⚠️')
                    ? 'bg-yellow-100 border-yellow-500 text-yellow-800'
                    : 'bg-red-100 border-red-500 text-red-700'
                    }`}>
                    <div className="flex items-start space-x-2">
                        {error.includes('⚠️') ? (
                            <AlertCircle className="w-5 h-5 mt-0.5 text-yellow-600" />
                        ) : (
                            <AlertCircle className="w-5 h-5 mt-0.5 text-red-600" />
                        )}
                        <span className="font-medium">{error}</span>
                    </div>
                </div>
            )}

            {/* Exercise Interface - Same as before but with small improvements */}
            {exercises.length > 0 && currentExercise && (
                <div>
                    {/* Progress & Stats Bar */}
                    <div className="mb-6 p-4 rounded-xl bg-blue-50 border border-blue-200">
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center space-x-4">
                                <span className="text-lg font-semibold text-gray-800">
                                    Câu {currentExerciseIndex + 1}/{exercises.length}
                                </span>
                                <div className="flex items-center space-x-2">
                                    <Clock className="w-4 h-4 text-orange-600" />
                                    <span className="font-mono text-orange-600">
                                        {formatTime(timer)}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Target className="w-4 h-4 text-green-600" />
                                    <span className="text-green-600">
                                        {score.correct}/{score.total} ({accuracy}%)
                                    </span>
                                </div>
                                {/* 🎯 Show current exercise focus */}
                                {currentExercise.focus && (
                                    <div className="flex items-center space-x-2">
                                        <BookOpen className="w-4 h-4 text-purple-600" />
                                        <span className="text-xs text-purple-600">
                                            {currentExercise.focus}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full h-2 rounded-full bg-gray-300">
                            <div
                                className="h-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {/* Exercise Card */}
                    <div className="p-5 rounded-xl shadow-lg bg-white border border-gray-200">
                        {/* Question */}
                        <div className="mb-5">
                            <h3 className="text-xl font-bold mb-2 text-gray-800">
                                📝 Câu hỏi:
                            </h3>
                            <p className="text-lg leading-relaxed text-gray-700">
                                {currentExercise.question}
                            </p>

                            {/* 🎯 Show explanation if available and answer is checked */}
                            {isAnswerChecked && currentExercise.explanation && (
                                <div className="mt-4 p-4 rounded-lg border-l-4 border-green-500 bg-green-50">
                                    <h4 className="font-semibold mb-2 text-green-700">
                                        💡 Giải thích:
                                    </h4>
                                    <p className="text-gray-700">
                                        {currentExercise.explanation}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Options */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
                            {Object.entries(currentExercise.options).map(([key, value]) => {
                                let optionClass = `p-3 rounded-lg border-2 cursor-pointer transition-all transform hover:scale-102 border-gray-300`;

                                const userAnswer = userAnswers[currentExerciseIndex];
                                if (!isAnswerChecked) {
                                    // Styling for when user is selecting an answer
                                    if (selectedOption === key) {
                                        optionClass += ` bg-blue-100 ring-2 ring-blue-500 border-blue-500`;
                                    } else {
                                        optionClass += ` bg-white hover:bg-gray-100`;
                                    }
                                } else {
                                    // Styling after answer is checked
                                    if (key === currentExercise.correct_answer) {
                                        // The correct answer is always highlighted in green
                                        optionClass += ` bg-green-100 border-green-500 text-green-800`;
                                    } else if (selectedOption === key) {
                                        // The user's wrong choice is highlighted in red
                                        optionClass += ` bg-red-100 border-red-500 text-red-800`;
                                    } else {
                                        // Other incorrect options are neutral and faded
                                        optionClass += ` bg-gray-50 opacity-50`;
                                    }
                                }

                                return (
                                    <div
                                        key={key}
                                        className={optionClass}
                                        onClick={() => handleOptionClick(key)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className="font-bold text-lg">{key}.</span> {value}
                                            </div>
                                            {/* ✅ Show icons for correct/incorrect after checking */}
                                            {isAnswerChecked && key === currentExercise.correct_answer && (
                                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
                                            )}
                                            {isAnswerChecked && key === selectedOption && userAnswer && !userAnswer.correct && (
                                                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 ml-2" />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            {/* Check Answer Button */}
                            {!isAnswerChecked && (
                                <button
                                    onClick={checkAnswer}
                                    disabled={!selectedOption}
                                    className={`flex-1 py-4 text-lg font-semibold rounded-xl transition-all ${!selectedOption
                                        ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                                        : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transform hover:scale-105'
                                        } text-white shadow-lg`}
                                >
                                    🎯 Kiểm Tra Đáp Án
                                </button>
                            )}

                            {/* Navigation Buttons */}
                            {isAnswerChecked && (
                                <div className="flex gap-4">
                                    <button
                                        onClick={prevExercise}
                                        disabled={currentExerciseIndex === 0}
                                        className="bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                        Trước
                                    </button>

                                    {currentExerciseIndex < exercises.length - 1 ? (
                                        <button
                                            onClick={nextExercise}
                                            className="flex items-center gap-2 px-6 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all transform hover:scale-105"
                                        >
                                            Tiếp
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setShowResults(true)}
                                            className="flex items-center gap-2 px-6 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all transform hover:scale-105"
                                        >
                                            <Trophy className="w-5 h-5" />
                                            Xem Kết Quả
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 📊 Quick Stats */}
                        {isAnswerChecked && (
                            <div className="mt-4 p-3 rounded-lg bg-gray-100 border border-gray-200">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">
                                        Câu đã trả lời: {answeredQuestions.size}/{exercises.length}
                                    </span>
                                    <span className="text-gray-600">
                                        Còn lại: {exercises.length - answeredQuestions.size} câu
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

                </div>
            </div>
        </div>
    );
};

export default ExerciseGenerator;